import type { StyleProp, TextStyle, ViewStyle } from 'react-native';

import React, { FC, useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import colors from '@packages/core/styles/colors';
import { Button, Icon, Typography } from '@packages/ui/shared';

import { DropdownButton } from '../dropdown-button';
import Select from '../select';

/** How the bar is drawn: as today, or as the design system's Paginator. */
export type TablePaginationLook = 'default' | 'tokens';

const useTablePaginationStyles = createUseStyles({
    pagination: {
        flexShrink: 0,
        display: 'flex',
        padding: '16px 0',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    },
    side: {
        flex: 1,
    },
    buttonGroup: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    active: {
        color: `${colors.black} !important`,
    },
});

const styles = StyleSheet.create({
    tokensButton: {
        borderRadius: 4,
        paddingHorizontal: 8,
    },
    tokensButtonText: {
        fontWeight: '600',
        lineHeight: 24,
        paddingHorizontal: 8,
    },
    tokensCount: {
        fontWeight: '600',
        lineHeight: 24,
    },
    tokensIcon: {
        marginLeft: 0,
        marginRight: 0,
    },
});

/** The parts that differ between the looks: the text and button styles, the sides and the page size control. */
type TablePaginationLookParts = {
    /** The corners and side padding of the buttons. */
    buttonStyle?: StyleProp<ViewStyle>;
    /** The text style inside the buttons. */
    buttonTextStyle?: StyleProp<TextStyle>;
    /** The text style of the count. */
    countStyle?: StyleProp<TextStyle>;
    /** Whether both sides of the buttons share the room, which keeps the buttons in the middle. */
    hasEvenSides: boolean;
    /** The spacing of the button icons. */
    iconStyle?: StyleProp<ViewStyle>;
    /** The control that changes the page size. */
    pageSizeControl: 'menu' | 'select';
};

/**
 * The parts of every look. The default is the bar as it has always been drawn; the tokens look is the
 * design system's Paginator: a Body/Semibold count, 40px buttons with 4px corners, 8px around and 8px
 * at each side of their text, in the middle of the bar, and the page size as an outlined menu button.
 */
const LOOKS: Record<TablePaginationLook, TablePaginationLookParts> = {
    default: {
        hasEvenSides: false,
        pageSizeControl: 'select',
    },
    tokens: {
        buttonStyle: styles.tokensButton,
        buttonTextStyle: styles.tokensButtonText,
        countStyle: styles.tokensCount,
        hasEvenSides: true,
        iconStyle: styles.tokensIcon,
        pageSizeControl: 'menu',
    },
};

/** The id of the bar unless the owner names it; the page size menu derives its own from it. */
const DEFAULT_ID = 'table-pagination';

interface TablePaginationProps {
    page: number;
    size: number;
    total: number;
    displayCount?: string;
    prevDisable?: boolean;
    nextDisable?: boolean;
    disabled?: boolean;
    /** Identifies the bar; the page size menu of the tokens look ties itself to `<id>-page-size`. */
    id?: string;
    /** The look of the bar; the default keeps today's spacing, type and native select. */
    look?: TablePaginationLook;
    pageNumberDisable?: boolean;
    onClick(pageIndex: number): void;
    onSizeChange(pageSize: number): void;
    pageSizeOptions?: number[];
}

export const TablePagination: FC<TablePaginationProps> = React.memo(
    ({
        displayCount,
        page,
        size,
        total,
        prevDisable,
        nextDisable,
        disabled,
        onClick,
        onSizeChange,
        pageSizeOptions,
        id = DEFAULT_ID,
        look = 'default',
    }) => {
        const classes = useTablePaginationStyles();
        const parts = LOOKS[look];
        const sideClassName = parts.hasEvenSides ? classes.side : undefined;
        const [isSizeMenuOpen, setIsSizeMenuOpen] = useState(false);
        const buttonIndices: number[] = Array.from(Array(Math.ceil(total / size)).keys());
        const lastPageIndex = buttonIndices.length - 1 >= 0 ? buttonIndices.length - 1 : 0;
        const options = pageSizeOptions && pageSizeOptions.length > 0 ? pageSizeOptions : [10, 25, 50];

        function getPageButtonsToShow() {
            let pageArray = [];

            if (page > 0 && page < lastPageIndex) {
                pageArray = [page];

                if (buttonIndices[page + 1] !== undefined) {
                    pageArray.push(page + 1);
                }
                if (buttonIndices[page - 1] !== undefined) {
                    pageArray.unshift(page - 1);
                }

                return pageArray;
            }

            if (page === 0) {
                pageArray = [page];

                if (buttonIndices[page + 1] !== undefined) {
                    pageArray.push(page + 1);
                }
                if (buttonIndices[page + 2] !== undefined) {
                    pageArray.push(page + 2);
                }

                return pageArray;
            }

            if (page === lastPageIndex) {
                pageArray = [page];

                if (buttonIndices[page - 1] !== undefined) {
                    pageArray.unshift(page - 1);
                }
                if (buttonIndices[page - 2] !== undefined) {
                    pageArray.unshift(page - 2);
                }

                return pageArray;
            }

            return [];
        }

        function handlePreviousButtonClick() {
            const previousPage = page - 1;

            if (previousPage < 0) {
                return;
            }

            onClick(previousPage);
        }

        function handlePaginationButtonClick(buttonIndex: number) {
            if (buttonIndex === page) {
                return;
            }

            onClick(buttonIndex);
        }

        function handleNextButtonClick() {
            const nextPage = page + 1;

            if (nextPage > lastPageIndex) {
                return;
            }

            onClick(nextPage);
        }

        function handlePageSizeSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
            onSizeChange(parseInt(event.currentTarget.value, 10));
        }

        const handlePageSizeMenuSelect = useCallback(
            (key: string) => onSizeChange(Number.parseInt(key, 10)),
            [onSizeChange]
        );

        /**
         * The page size control of the look: the menu button or the native select.
         *
         * @returns the control
         */
        function renderPageSizeControl() {
            if (parts.pageSizeControl === 'menu') {
                return (
                    <DropdownButton
                        iconRight="chevron-down"
                        id={`${id}-page-size`}
                        isDisabled={disabled}
                        isOpen={isSizeMenuOpen}
                        items={options.map((option) => ({ key: String(option), label: `${option} per page` }))}
                        onSelect={handlePageSizeMenuSelect}
                        onToggle={setIsSizeMenuOpen}
                        title={`${size} per page`}
                    />
                );
            }

            return (
                <Select
                    value={String(size)}
                    options={options.map((option) => ({
                        value: String(option),
                        title: `${option} per page`,
                    }))}
                    onChange={handlePageSizeSelectChange}
                    disabled={disabled}
                />
            );
        }

        return (
            <div className={classes.pagination}>
                <div className={sideClassName}>
                    <Typography variant="label" color="blueOne" style={parts.countStyle}>
                        {displayCount}
                    </Typography>
                </div>
                <div className={classes.buttonGroup}>
                    <Button
                        variant="ghost-blue"
                        onPress={handlePreviousButtonClick}
                        disabled={page === 0 || prevDisable || disabled}
                        iconLeft={<Icon name="chevron-left" color="blueOne" />}
                        iconLeftStyle={parts.iconStyle}
                        style={parts.buttonStyle}
                        titleStyle={parts.buttonTextStyle}
                    >
                        Prev
                    </Button>
                    {getPageButtonsToShow().map((buttonIndex) => {
                        return (
                            <Button
                                key={buttonIndex}
                                variant={buttonIndex === page ? 'primary' : 'ghost-blue'}
                                onPress={() => {
                                    handlePaginationButtonClick(buttonIndex);
                                }}
                                disabled={disabled}
                                style={parts.buttonStyle}
                            >
                                <Typography
                                    variant="label"
                                    color={buttonIndex === page ? 'white' : 'blueOne'}
                                    style={parts.buttonTextStyle}
                                >
                                    {buttonIndex + 1}
                                </Typography>
                            </Button>
                        );
                    })}
                    <Button
                        variant="ghost-blue"
                        onPress={handleNextButtonClick}
                        disabled={page === lastPageIndex || nextDisable || disabled}
                        iconRight={<Icon name="chevron-right" color="blueOne" />}
                        iconRightStyle={parts.iconStyle}
                        style={parts.buttonStyle}
                        titleStyle={parts.buttonTextStyle}
                    >
                        Next
                    </Button>
                    {renderPageSizeControl()}
                </div>
                <div className={sideClassName} />
            </div>
        );
    }
);

TablePagination.displayName = 'TablePagination';
